import { useState, useEffect } from "react";
import { X, AlertCircle } from "lucide-react";

import Field from "../field";
import { INTERACTION_TYPES } from "../consts";
import {
  callApi,
  getCourseLabel,
  getGroupRequestBody,
  getVariantsMapping,
  updateSubgroupVariants,
} from "../utils";
import EditPracticals from "./edit-practicals";
import EditPracticalsCount from "./edit-practicals-count";
import EditSubgroups from "./edit-subgroups";
import EditVariantsAssignment from "./edit-variants-assignment";

/* ---------------------------------------------------------------------- */
/*  Мастер создания группы                                                */
/* ---------------------------------------------------------------------- */

export default function GroupWizard({ onCancel, onCreate }) {
  const [cources, setCources] = useState([]);
  const [course, setCourse] = useState("");
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupNumber, setGroupNumber] = useState("");
  const [interactionType, setInteractionType] = useState("");
  const [subgroupsCount, setSubgroupsCount] = useState(1);
  const [students, setStudents] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [practicalsCount, setPracticalsCount] = useState(1);
  const [practicals, setPracticals] = useState([]);
  const [errors, setErrors] = useState([]);
  const [studentVariants, setStudentVariants] = useState([]);
  const [subgroupVariants, setSubgroupVariants] = useState([]);

  const isIndividual = interactionType === INTERACTION_TYPES.individual;
  const isGroup = interactionType === INTERACTION_TYPES.group;

  useEffect(() => {
    callApi({
      method: "GET",
      path: "api/teacher/courses",
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setCources(respArr);
        } else {
          console.error(respArr);
        }
      }
    });
  }, []);

  useEffect(() => {
    setGroupNumber("");
    setAvailableGroups([]);

    if (!course) {
      return;
    }

    const queryParams = new URLSearchParams({ course });

    callApi({
      method: "GET",
      path: "api/teacher/groups",
      needAuth: true,
      queryParams,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setAvailableGroups(respArr);
        } else {
          console.error(respArr);
        }
      }
    });
  }, [course]);

  useEffect(() => {
    setStudents([]);

    if (!groupNumber) {
      return;
    }

    const queryParams = new URLSearchParams({
      course,
      group: groupNumber,
    });

    callApi({
      method: "GET",
      path: "api/teacher/students",
      needAuth: true,
      queryParams,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setStudents(respArr);
        } else {
          console.error(respArr);
        }
      }
    });
  }, [groupNumber]);

  const updateStudentVariants = () =>
    setStudentVariants(
      students.map((student, i) => ({
        studentId: student.id,
        fullName: student.fullName,
        practices: getVariantsMapping(practicals, i),
      })),
    );

  useEffect(() => {
    if (isIndividual) {
      updateStudentVariants();
    }
  }, [students]);

  const validate = () => {
    const errs = [];
    if (!course) errs.push("Укажите курс.");
    if (!groupNumber) errs.push("Выберите номер группы.");
    if (!interactionType) errs.push("Выберите тип взаимодействия.");
    practicals.forEach((p) => {
      if (!p.startDate || !p.endDate)
        errs.push(
          `Заполните период выполнения для практической работы № ${p.number}.`,
        );
      else if (p.startDate > p.endDate)
        errs.push(
          `Дата завершения практической работы № ${p.number} раньше даты начала.`,
        );
    });
    return errs;
  };

  const submit = () => {
    const errs = validate();
    setErrors(errs);
    if (errs.length) return;

    const body = getGroupRequestBody({
      course: Number(course),
      group: groupNumber,
      interactionType,
      practicals,
      ...(isGroup
        ? {
            students: subgroups,
            subgroupVariants,
          }
        : { studentVariants }),
    });

    callApi({
      method: "POST",
      path: "api/teacher/practice-groups",
      body,
      needAuth: true,
    }).then((response) => {
      if (response.error) {
        const details = response.details;

        const newErrors = details.length
          ? details
          : [response.message ?? "При добавлении группы произошла ошибка"];

        setErrors(newErrors);
      } else {
        const respObj = response.response;
        if (respObj) {
          onCreate(respObj.id);
        } else {
          setErrors(["Некорректный ответ с сервера"]);
        }
      }
    });
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal wizard">
        <div className="modal-head">
          <h2>Добавить группу</h2>
          <button className="icon-btn" onClick={onCancel} aria-label="Закрыть">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="grid-2">
            <Field label="Курс" required>
              <select
                value={course}
                onChange={(e) => setCourse(e.target.value)}
              >
                <option value="">Выберите курс</option>
                {cources.map((c) => (
                  <option key={c} value={c}>
                    {getCourseLabel(c)}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Номер группы"
              required
              hint={course ? undefined : "Сначала выберите курс"}
            >
              <select
                value={groupNumber}
                onChange={(e) => setGroupNumber(e.target.value)}
                disabled={!course}
              >
                <option value="">Выберите группу</option>
                {availableGroups.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Тип взаимодействия" required>
            <div className="radio-row">
              <label className={"radio-card" + (isIndividual ? " active" : "")}>
                <input
                  type="radio"
                  name="itype"
                  checked={isIndividual}
                  onChange={() => {
                    setInteractionType(INTERACTION_TYPES.individual);
                    setSubgroupVariants([]);
                    updateStudentVariants();
                  }}
                />
                <span>Индивидуальный</span>
                <em>
                  каждый студент выполняет практическую работу самостоятельно
                </em>
              </label>
              <label className={"radio-card" + (isGroup ? " active" : "")}>
                <input
                  type="radio"
                  name="itype"
                  checked={isGroup}
                  onChange={() => {
                    setInteractionType(INTERACTION_TYPES.group);
                    setStudentVariants([]);
                    updateSubgroupVariants(
                      subgroupsCount,
                      practicals,
                      setSubgroupVariants,
                    );
                  }}
                />
                <span>Групповой</span>
                <em>
                  студенты разбиты на подгруппы, работа выполняется коллективно
                </em>
              </label>
            </div>
          </Field>

          {isGroup && (
            <EditSubgroups
              students={students}
              subgroupsCount={subgroupsCount}
              setSubgroupsCount={setSubgroupsCount}
              subgroups={subgroups}
              setSubgroups={setSubgroups}
            />
          )}

          <EditPracticalsCount
            practicalsCount={practicalsCount}
            setPracticalsCount={setPracticalsCount}
            setPracticals={setPracticals}
          />

          <EditPracticals
            practicals={practicals}
            setPracticals={setPracticals}
          />

          {interactionType &&
            ((isIndividual && students.length > 0) ||
              (isGroup && subgroups.length > 0)) && (
              <EditVariantsAssignment
                isGroup={isGroup}
                practicals={practicals}
                subgroupsCount={subgroupsCount}
                studentVariants={studentVariants}
                setStudentVariants={setStudentVariants}
                subgroupVariants={subgroupVariants}
                setSubgroupVariants={setSubgroupVariants}
              />
            )}

          {errors.length > 0 && (
            <div className="error-box">
              {errors.map((e, i) => (
                <div key={i}>
                  <AlertCircle size={14} /> {e}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onCancel}>
            Отмена
          </button>
          <button className="btn primary" onClick={submit}>
            Создать группу
          </button>
        </div>
      </div>
    </div>
  );
}
